import {Component, OnInit, OnChanges, Input, inject, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {ContentService, SupplementalContentItem} from '@wellfit/wf-plans-services';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';

@Component({
  selector: 'app-ancillary-benefits',
  templateUrl: './ancillary-benefits.component.html',
  styleUrls: ['./ancillary-benefits.component.scss'],
  standalone: false
})

export class AncillaryBenefitsComponent implements OnInit, OnChanges {
  private _destroyRef: DestroyRef = inject(DestroyRef);
  private _domSanitizer: DomSanitizer = inject(DomSanitizer);
  private _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private _router: Router = inject(Router);
  private _contentService: ContentService = inject(ContentService);

  @Input() public ancillaryCodes: string = '';
  public activeTab: string = 'dental';
  public currentBonusServiceHtml: string = '';
  public visibleTabs: { [key: string]: boolean } = {
    vision: false,
    lasik: false,
    hearing: false,
    prescriptions: false,
    urgentcare: false,
    therapy: false
  };

  private _codeToSection: { [key: string]: string } = {
    // Vision
    'EYEM': 'vision',
    'VSCA': 'vision',
    'VSPS': 'vision',
    // Lasik
    'LCA': 'lasik',
    'AMST': 'lasik',
    // Hearing
    'HRPO': 'hearing',
    'BLTN': 'hearing',
    // Prescriptions
    'ENVC': 'prescriptions',
    'MEDI': 'prescriptions',
    'UNOA': 'prescriptions',
    'WELD': 'prescriptions',
    'QBLL': 'prescriptions',
    'PRFL': 'prescriptions',
    'NSDI': 'prescriptions',
    'BEN': 'prescriptions',
    'DLAO': 'prescriptions',
    'WDAV': 'prescriptions',
    // Urgent Care
    'DCCF': 'urgentcare',
    'LSRC': 'urgentcare',
    'WADCCF-40': 'urgentcare',
    'DCNC': 'urgentcare',
    'USIN': 'urgentcare',
    // Therapy
    'DMCF': 'therapy',
    'DMNC': 'therapy',
    'DMCF-70': 'therapy'
  };

  public ngOnChanges(): void {
    this._updateVisibleTabs();
    this._setDefaultActiveTab();
    if (this.activeTab) {
      this._loadBonusServiceContent(this.activeTab);
    }
  }

  public ngOnInit(): void {
    this._updateVisibleTabs();
    this._activatedRoute.fragment
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(fragment => {
        if (fragment) {
          const tabMap: { [key: string]: string } = {
            'nav-vision': 'vision',
            'nav-lasik': 'lasik',
            'nav-prescriptions': 'prescriptions',
            'nav-hearing': 'hearing',
            'nav-urgentcare': 'urgentcare',
            'nav-therapy': 'therapy'
          };
          const tab = tabMap[fragment];
          if (tab && this.visibleTabs[tab]) {
            this.activeTab = tab;
            this._loadBonusServiceContent(tab);
          }
        } else {
          this._setDefaultActiveTab();
          if (this.activeTab) {
            this._loadBonusServiceContent(this.activeTab);
          }
        }
      });
  }

  private _updateVisibleTabs(): void {
    Object.keys(this.visibleTabs).forEach(key => this.visibleTabs[key] = false);

    if (this.ancillaryCodes) {
      const codes = this.ancillaryCodes.split('|');
      codes.forEach(code => {
        const section = this._codeToSection[code.trim()];
        if (section) {
          this.visibleTabs[section] = true;
        }
      });
    }
  }

  private _setDefaultActiveTab(): void {
    const visibleTabKeys = Object.keys(this.visibleTabs).filter(key => this.visibleTabs[key]);
    if (visibleTabKeys.length > 0) {
      this.activeTab = visibleTabKeys[0];
    } else {
      this.activeTab = '';
    }
  }

  public switchTab(tab: string, fragment: string): void {
    if (this.visibleTabs[tab]) {
      this.activeTab = tab;

      this._router.navigate([], {
        fragment: fragment,
        relativeTo: this._activatedRoute
      });

      this._loadBonusServiceContent(tab);
    }
  }

  private _loadBonusServiceContent(tab: string): void {
    const availableCodes = this.ancillaryCodes ? this.ancillaryCodes.split('|') : [];
    const codeForTab = availableCodes.find(code => {
      const section = this._codeToSection[code.trim()];
      return section === tab;
    });

    if (codeForTab) {
      this._contentService.getBonusServices(codeForTab.trim())
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (items: SupplementalContentItem[]) => {
            try {
              const parsed = JSON.parse(items[0]?.content || '{}');
              this.currentBonusServiceHtml = parsed.HtmlBodyPart?.Html || '';
            } catch {
              this.currentBonusServiceHtml = items[0]?.content || '';
            }
          },
          error: () => {
            this.currentBonusServiceHtml = '<p>Content not available at this time.</p>';
          }
        });
    }
  }

  public trustHtml(html: string): SafeHtml {
    return this._domSanitizer.bypassSecurityTrustHtml(html);
  }
}
